import IconAgentTimeout from "@/icons/IconAgentTimeout";
import IconArrowSquareDownRight from "@/icons/IconArrowSquareDownRight";
import IconCalendar from "@/icons/IconCalendar";
import IconDownload from "@/icons/IconDownload";
import IconFileCheck from "@/icons/IconFileCheck";
import IconLink from "@/icons/IconLink";
import IconMessageChatSquare from "@/icons/IconMessageChatSquare";
import IconMessageCheckSquare from "@/icons/IconMessageCheckSquare";
import IconMessageNotificationSquare from "@/icons/IconMessageNotificationSquare";
import IconOutgoingMessageSquare from "@/icons/IconOutgoingMessageSquare";
import IconTicket from "@/icons/IconTicket";
import IconUnresponsiveAgentSquare from "@/icons/IconUnresponsiveAgentSquare";
import IconUnresponsiveVisitorSquare from "@/icons/IconUnresponsiveVisitorSquare";
import IconUpload from "@/icons/IconUpload";
import IconUsersPlus from "@/icons/IconUsersPlus";
import { Modal, ModalBody, ModalHeader } from "@velaro/velaro-component-lib";
import { IconProps } from "@velaro/velaro-component-lib";
import * as React from "react";
import { PremiumTag } from "../../../components/PremiumTag";
import { ComingSoonTag } from "../../../components/ComingSoonTag";

interface Props {
  onClose: () => void;
}

const triggers = [
  {
    icon: IconMessageChatSquare,
    title: "Conversation Opened",
    text: "When starting a new conversation with the customer.",
    id: "conversation_opened"
  },
  {
    icon: IconMessageNotificationSquare,
    title: "Incoming Message",
    text: "When receiving a message from a visitor.",
    id: "incoming_message",
    comingSoon: true
  },
  {
    icon: IconOutgoingMessageSquare,
    title: "Outgoing Message",
    text: "When sending a message to a visitor.",
    id: "outgoing_message",
    comingSoon: true
  },
  {
    icon: IconMessageCheckSquare,
    title: "Conversation Resolved",
    text: "When a conversation is marked resolved or compelted.",
    id: "conversation_resolved",
    comingSoon: true
  },
  {
    icon: IconUnresponsiveAgentSquare,
    title: "Unresponsive Agent",
    text: "When an agent hasn't responded within a set timeframe.",
    id: "unresponsive_agent",
    comingSoon: true
  },
  {
    icon: IconUnresponsiveVisitorSquare,
    title: "Unresponsive Visitor",
    text: "When a visitor doesn't respond during a conversation.",
    id: "unresponsive_visitor",
    comingSoon: true
  },
  {
    icon: IconLink,
    title: "Visitor Opens a Web Page",
    text: "When a visitor lands on a specific website page.",
    id: "visitor_opens_page",
    comingSoon: true
  },
  {
    icon: IconFileCheck,
    title: "Form Submitted",
    text: "When a visitor submits a form.",
    id: "form_submitted",
    comingSoon: true
  },
  {
    icon: IconUsersPlus,
    title: "Conversation Assigned To",
    text: "When assigning a conversation to an agent or team.",
    id: "conversation_assigned_to",
    comingSoon: true
  },
  {
    icon: IconUpload,
    title: "File Uploaded",
    text: "When a file is uploaded from the conversation.",
    id: "file_uploaded",
    comingSoon: true
  },
  {
    icon: IconDownload,
    title: "File Downloaded",
    text: "When a file is downloaded from the conversation.",
    id: "file_downloaded",
    comingSoon: true
  },
  {
    icon: IconAgentTimeout,
    title: "Agent Timeout",
    text: "When the conversation exceeds the wait threshold.",
    id: "agent_timeout",
    comingSoon: true
  },
  {
    icon: IconCalendar,
    title: "Appointment Scheduled",
    text: "When an appointment is scheduled in the system.",
    id: "appointment_scheduled",
    premium: true,
    comingSoon: true
  },
  {
    icon: IconTicket,
    title: "Service Ticket Created",
    text: "When a user reports an issue or requests support.",
    id: "service_ticket_created",
    premium: true,
    comingSoon: true
  },
  {
    icon: IconArrowSquareDownRight,
    title: "Visitor Returns to the Site",
    text: "When an existing user returns to the site.",
    id: "visitor_returns_to_site",
    premium: true,
    comingSoon: true
  }
];

interface Props {
  onClose: () => void;
  onSelect(triggerId: string): void;
}

export default function TriggerModal(props: Props) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <Modal maxWidth={"max-w-7xl"} show={true}>
        <ModalHeader title="Select a Trigger" onClose={props.onClose} />
        <ModalBody>
          <div className="-mt-3">
            <span className="text-sm text-gray-500">
              Triggers are specific events or conditions that, once met,
              kick-start your workflow
            </span>
            <hr className="my-4 border-gray-200" />
            {/* <div className="flex flex-wrap gap-4"> */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {triggers.map((trigger, index) => (
                <TriggerCard
                  onClick={() => {
                    if (!trigger.comingSoon) {
                      props.onSelect(trigger.id);
                    }
                  }}
                  key={index}
                  {...trigger}
                />
              ))}
            </div>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
}

function TriggerCard(props: {
  icon: ({ color, ...props }: IconProps) => React.JSX.Element;
  title: string;
  text: string;
  premium?: boolean;
  comingSoon?: boolean;
  onClick?: () => void;
}) {
  const isDisabled = props.comingSoon;

  return (
    <div
      onClick={isDisabled ? undefined : props.onClick}
      className={`group p-4 border rounded-md ${
        isDisabled
          ? "bg-gray-100 border-gray-200 cursor-not-allowed"
          : "bg-white border-gray-200 hover:border-cornflower-blue-500 hover:shadow-sm hover:cursor-pointer"
      }`}
    >
      <div className="flex justify-between items-center">
        <props.icon
          className={`${
            isDisabled
              ? "stroke-gray-400 fill-gray-400"
              : "stroke-slate-500 fill-slate-500 group-hover:stroke-cornflower-blue-500 group-hover:fill-cornflower-blue-500"
          }`}
        />
        {props.comingSoon && <ComingSoonTag />}
        {props.premium && !props.comingSoon && <PremiumTag />}
      </div>
      <div>
        <div className={`font-semibold py-2 ${
          isDisabled ? "text-black" : "group-hover:text-cornflower-blue-500"
        }`}>
          {props.title}
        </div>
        <div className="text-sm text-gray-500">{props.text}</div>
      </div>
    </div>
  );
}
